// @flow

import React from 'react';
import { I18n, Translate } from 'react-redux-i18n';
import type { kpiData, StoreGemini, VisioglobeUnitsMap, YearMonth } from '../../../../types/geminiType';
import { download, intRange } from '../../../../utils/utils';
import { getCurrentPcId } from '../../../../utils/urlUtils';
import { getCurrentYear } from '../../../../utils/dateUtils';
import { formatYearMonth } from '../../../../utils/formatUtils';
import geminiApi from '../../../../network/api/geminiApi';
import Analysis from './Analysis';
import IconButton from '../../../../components/IconButton';
import { reconciliationStates, shop } from '../../../../const';
import { STORE_INFO } from '../../../../path/storePath';
import { getAllIdVisioglobesUnits, isStoreDisplayable } from './geminiFunctions';
import StoreDescription from './StoreDescription';
import EmptyUnitMapping from './EmptyUnitMapping';
import type { MapViewer } from '../../../../types/VisioglobeType';
import { trackAction } from '../../../../components/GoogleAnalyticsWrapper';
import GoodwillButton from './GoodwillButton';
import type { ShoppingCenter } from '../../../../types/shoppingCenterType';
import SelectComponent from '../../../common/SelectComponent';
import { notifyApolloError } from '../../../../network/notification';

type Props = {
  dispatch: () => void,
  history: History,
  shoppingCenter: ShoppingCenter,
  mapviewer: MapViewer,
  visioglobeUnitsMap: VisioglobeUnitsMap,
  updateVisioglobeUnits: (year: string) => void,
  changeMappingMode: Function,
  business: kpiData[],
  selectedStore: StoreGemini,
  title: string,
  selectedCategory: string,
  storeBranchLevel: string,
  categoryOverride: boolean,
  onClose: Function,
  yearMonth: YearMonth,
  showComparable: Function,
  showChart: Function,
  isButton: boolean,
  isGeminiAdmin: boolean,
  isPulseAuthorized: boolean,
}

type State = {
  idsTopRetailers: Array<number>,
  isM2Loading: boolean,
  isStoreDistancesLoading: boolean,
  isGeminiAdmin: boolean,
}
const DL_M2_2 = 'DL_M2.2';

class GeminiSideData extends React.Component<Props, State> {
  state: State = {
    idsTopRetailers: [],
    isM2Loading: false,
    isStoreDistancesLoading: false,
  };

  updateYear = (year: string) => {
    this.props.updateVisioglobeUnits(year);
  };

  getYearMonthList = () => {
    const currentYear = getCurrentYear();
    const currentMonth = this.props.yearMonth.month;
    return intRange(currentYear - 2, currentYear).map(year => ({
      value: (year).toString(),
      label: formatYearMonth(year, currentMonth),
    }));
  };

  categoryHasBeenOverriddenOrIsShop = () => (
    this.props.categoryOverride || this.props.selectedCategory === shop
  );

  storeHasCodstr = () => (this.props.selectedStore
    && (this.props.selectedStore.codstr));

  fetchM2Report = () => {
    this.setState({
      isM2Loading: true,
    });

    trackAction(DL_M2_2);
    geminiApi.fetchM2Report(
      getCurrentPcId(),
      this.props.selectedStore.retailerId,
      this.props.yearMonth.year,
      this.props.yearMonth.month,
    )
      .then(response => response.blob())
      .then(responseBlob => download(
        responseBlob,
        I18n.t('SIDE_DATA.m2_report_filename', { retailerId: this.props.selectedStore.retailerId }),
      )).catch(e => this.props.dispatch(notifyApolloError(e)))
      .finally(() => this.setState({ isM2Loading: false }));
  };

  getTitle = () => {
    if (this.props.selectedStore && this.props.selectedStore.isRental) {
      return I18n.t('SIDE_DATA.rental_space', { idVisioglobes: this.props.selectedStore.idVisioglobes.join(', ') });
    }
    if (this.props.title) {
      return this.props.title;
    }
    if (this.props.selectedStore) {
      return I18n.t('SIDE_DATA.EMPTY_UNIT.title', { idVisioglobes: this.props.selectedStore.idVisioglobes.join(', ') });
    }
    return 'N/A';
  };

  getSubTitle = () => {
    if (this.props.title) {
      return this.props.title;
    }
    if (this.props.selectedStore) {
      return I18n.t('SIDE_DATA.EMPTY_UNIT.subtitle');
    }
    return '';
  };

  allStoreDistancesBean = (routes) => {
    const visioglobeUnits = Object.entries(this.props.visioglobeUnitsMap)
      .flatMap(entry => entry[1])
      .map((entry) => {
        const res = {};
        res.idVisioglobes = entry.idVisioglobes;
        res.codstr = entry.codstr;
        res.state = entry.state;
        return res;
      });
    const filteredRoutes = routes.filter(entry => entry != null);
    return {
      routes: filteredRoutes,
      visioglobeUnits,
      pcCode: this.props.shoppingCenter.pcCode,
    };
  };

  computeAllStoreDistances = () => {
    this.setState({ isStoreDistancesLoading: true });
    const idVisioglobeUnits = getAllIdVisioglobesUnits(this.props.visioglobeUnitsMap);

    // Timeout is required to update the state before processing the promise
    setTimeout(
      () => this.props.mapviewer.searchRoutes(idVisioglobeUnits, idVisioglobeUnits)
        .then(response => geminiApi.saveStoreDistances(getCurrentPcId(), this.allStoreDistancesBean(response)))
        .catch(e => this.props.dispatch(notifyApolloError(e)))
        .finally(() => this.setState({ isStoreDistancesLoading: false })),
      1000,
    );
  };

  isATopRetailer = (retailerId: number) => this.state.idsTopRetailers.includes(retailerId);

  componentDidMount() {
    // storesApi.fetchTopRetailers()
    //   .then(response => response.json())
    //   .then(response => this.setState({ idsTopRetailers: response }));
  }

  render() {
    return (
      <div
        className="gemini-store-data-container gemini-store-data-container--overflow gemini-side-data"
      >
        <div className="store-description-container">
          <div className="store-name-container">
            <div className="placeholder placeholder--absolute">
              <button
                onClick={this.props.onClose}
                className="exit-button"
              >
                <i className="fal fa-chevron-right right-chevron" />
              </button>
            </div>
            <div className="store-name store-name--center">
              {
                this.props.selectedStore
                && this.props.selectedStore.isClosed
                && (
                  <Translate value="SIDE_DATA.isEx" />
                )
              }
              {this.getTitle()}
              {
                this.props.selectedStore
                && this.props.selectedStore.state === reconciliationStates.HISTORY
                && (
                  <>&nbsp;<Translate value="SIDE_DATA.OLD_TENANT" /></>
                )
              }
            </div>
            <div className="placeholder" />
          </div>
          {
            this.props.selectedStore && this.props.selectedStore.isRental && (
              <div className="store-subtitle">
                {this.getSubTitle()}
              </div>
            )
          }
          {
            this.storeHasCodstr()
            && this.categoryHasBeenOverriddenOrIsShop()
              ? (
                <div className="action-buttons-container">
                  <div className="btn-apollo-container">
                    <IconButton
                      icon="fal fa-file-download"
                      className="apollo-button-contained"
                      isLoading={this.state.isM2Loading}
                      onClick={this.fetchM2Report}
                      disabled={!this.props.isPulseAuthorized}
                      title={!this.props.isPulseAuthorized && I18n.t('SIDE_MENU.report_unauthorized')}
                    >
                      <Translate value="SIDE_DATA.m2_report" />
                    </IconButton>
                  </div>
                  {
                    this.props.isGeminiAdmin
                    && this.isATopRetailer(this.props.selectedStore.retailerId)
                    && (
                      <GoodwillButton
                        dispatch={this.props.dispatch}
                        shoppingCenter={this.props.shoppingCenter}
                        selectedCodstr={this.props.selectedStore.codstr}
                        selectedStoreName={this.props.selectedStore.storeName}
                        buttonName={<Translate value="SIDE_MENU.goodwill_report" />}
                      />
                    )
                  }
                  <div className="btn-apollo-container">
                    <IconButton
                      icon="fas fa-exchange"
                      className="apollo-button-contained rounded-button"
                      onClick={() => this.props.showComparable(true)}
                    >
                      <Translate value="SIDE_DATA.COMPARABLE" />
                    </IconButton>
                  </div>
                  <div className="btn-apollo-container">
                    <IconButton
                      icon="far fa-chart-bar"
                      className="apollo-button-contained rounded-button"
                      onClick={() => this.props.showChart(true)}
                    >
                      <Translate value="SIDE_DATA.CHART" />
                    </IconButton>
                  </div>
                  {
                    !this.props.shoppingCenter.pcCode.startsWith('US')
                    &&
                    (
                      this.props.selectedStore.state === reconciliationStates.HISTORY
                        ? (
                          <EmptyUnitMapping
                            dispatch={this.props.dispatch}
                            visioglobeUnitsMap={this.props.visioglobeUnitsMap}
                            updateVisioglobeUnits={this.props.updateVisioglobeUnits}
                            changeMappingMode={this.props.changeMappingMode}
                            selectedStore={this.props.selectedStore}
                          />
                        ) : (
                          <div className="btn-apollo-container mobile-hide">
                            <IconButton
                              icon="fas fa-medal"
                              className="apollo-button-contained rounded-button"
                              onClick={() =>
                                // $FlowFixMe
                                this.props.history.push(`${STORE_INFO}?store=${this.props.selectedStore.codstr}&mall=${getCurrentPcId()}`)
                              }
                            >
                              <Translate value="SIDE_DATA.apollo" />
                            </IconButton>
                          </div>
                        )
                    )
                  }
                </div>
              ) : (
                this.props.selectedStore
                && (isStoreDisplayable(this.props.selectedStore.state))
                && !this.props.shoppingCenter.pcCode.startsWith('US')
                && (
                  <div className="action-buttons-container">
                    <EmptyUnitMapping
                      dispatch={this.props.dispatch}
                      visioglobeUnitsMap={this.props.visioglobeUnitsMap}
                      updateVisioglobeUnits={this.props.updateVisioglobeUnits}
                      changeMappingMode={this.props.changeMappingMode}
                      selectedStore={this.props.selectedStore}
                    />
                  </div>
                )
              )
          }
          {
            (
              this.props.categoryOverride
              || (this.props.selectedCategory === shop && this.storeHasCodstr())
              || !this.categoryHasBeenOverriddenOrIsShop()
            ) && (
              <div className="analysis-container">
                <div className="analysis-container-block">
                  <div className="data-section-header">
                    <Translate value="SIDE_DATA.analysis" />
                    {
                      this.props.yearMonth
                      && (
                        <SelectComponent
                          handleChange={this.updateYear}
                          options={this.getYearMonthList()}
                          current={this.props.yearMonth.year.toString()}
                        />
                      )
                    }
                  </div>
                  <Analysis
                    selectedData={this.props.business}
                    isButton={this.props.isButton}
                  />
                </div>
                {
                  this.categoryHasBeenOverriddenOrIsShop()
                  && this.props.selectedStore
                  && (
                    <div className="analysis-container-block">
                      <div className="data-section-header">
                        <Translate value="SIDE_DATA.description" />
                      </div>
                      <StoreDescription
                        store={this.props.selectedStore}
                        storeBranchLevel={this.props.storeBranchLevel}
                      />
                    </div>
                  )
                }
              </div>
            )
          }
        </div>
        {/* { */}
        {/*  this.props.selectedCategory === shoppingCenter */}
        {/*  && isCurrentUserGeminiAdmin() */}
        {/*  && ( */}
        {/*    <IconButton */}
        {/*      className={ */}
        {/*        `ripple-button */}
        {/*        ${this.state.isStoreDistancesLoading ? 'disabled' : ''} */}
        {/*        apollo-button-contained */}
        {/*        compute-store-distances-button` */}
        {/*      } */}
        {/*      onClick={this.computeAllStoreDistances} */}
        {/*      isLoading={this.state.isStoreDistancesLoading} */}
        {/*      text={I18n.t('SIDE_DATA.COMPUTE_STORE_DISTANCES')} */}
        {/*    /> */}
        {/*  ) */}
        {/* } */}
      </div>
    );
  }
}

export default GeminiSideData;
